import { CARS, TRACKS, getCarById, getTrackById } from '../../content';
import type { GameController } from '../../core/GameController';
import { formatRaceTime } from '../../core/format';
import type { GameSnapshot } from '../../core/types';
import { getThemeLabel } from '../../render/environmentFactory';
import { ActionButton, BackButton, MenuFrame } from '../components/MenuFrame';
import { StatBars } from '../components/StatBars';

export function TrackSelectScreen({ controller, snapshot }: { controller: GameController; snapshot: GameSnapshot }) {
  const selected = getTrackById(snapshot.selectedTrackId);
  return (
    <div className="screen-layer selection-screen">
      <MenuFrame
        wide
        eyebrow="Course directory"
        title="Select Track"
        subtitle="Every course rewards a clean line, committed speed and quick recovery."
        footer={
          <div className="selection-footer">
            <BackButton onClick={() => controller.back()} />
            <ActionButton variant="primary" label="Choose Car" detail={selected.name} onClick={() => controller.confirmTrack()} />
          </div>
        }
      >
        <div className="track-selection-layout">
          <div className="track-grid" role="list" aria-label="Tracks">
            {TRACKS.map((track, index) => {
              const active = track.id === selected.id;
              return (
                <button
                  key={track.id}
                  role="listitem"
                  className={`track-card theme-${track.theme} ${active ? 'is-selected' : ''}`}
                  onClick={() => controller.selectTrack(track.id)}
                  aria-pressed={active}
                >
                  <span className="track-card__number">0{index + 1}</span>
                  <span className="track-card__route" aria-hidden="true"><i /><i /><i /></span>
                  <span className="track-card__body">
                    <b>{track.name}</b>
                    <small>{getThemeLabel(track.theme)}</small>
                  </span>
                  <span className={`difficulty difficulty--${track.difficulty.toLowerCase().replaceAll(' ', '-').replace('/', '')}`}>
                    {track.difficulty}
                  </span>
                  <span className="track-card__best">
                    <small>Personal best</small>
                    <b>{formatRaceTime(snapshot.bestTimes[track.id] ?? null)}</b>
                  </span>
                </button>
              );
            })}
          </div>
          <aside className={`track-detail theme-${selected.theme}`}>
            <div className="track-detail__topline">
              <span>{selected.difficulty}</span>
              <span>{Math.round(selected.targetTime[0])}–{Math.round(selected.targetTime[1])} sec target</span>
            </div>
            <div className="track-detail__map" aria-hidden="true">
              <span className="track-detail__map-glow" />
              <b>{selected.name.slice(0, 2).toUpperCase()}</b>
              <i /><i /><i /><i />
            </div>
            <p className="eyebrow">Featured course</p>
            <h2>{selected.name}</h2>
            <h3>{selected.subtitle}</h3>
            <p>{selected.description}</p>
            <div className="track-detail__feature">
              <span>Signature sector</span>
              <b>{selected.memorableFeature}</b>
            </div>
            <div className="track-detail__metrics">
              <span><small>Checkpoints</small><b>{selected.checkpointFractions.length}</b></span>
              <span><small>Boost pads</small><b>{selected.boosterFractions.length}</b></span>
              <span><small>Your best</small><b>{formatRaceTime(snapshot.bestTimes[selected.id] ?? null)}</b></span>
            </div>
          </aside>
        </div>
      </MenuFrame>
    </div>
  );
}

export function CarSelectScreen({ controller, snapshot }: { controller: GameController; snapshot: GameSnapshot }) {
  const selected = getCarById(snapshot.selectedCarId);
  const track = getTrackById(snapshot.selectedTrackId);
  return (
    <div className="screen-layer selection-screen car-selection-screen">
      <MenuFrame
        wide
        eyebrow="Forge garage"
        title="Select Car"
        subtitle={`Tune your approach for ${track.name}. Every stat changes the actual handling model.`}
        footer={
          <div className="selection-footer">
            <BackButton onClick={() => controller.back()} />
            <div className="selection-footer__summary">
              <small>Selected pairing</small>
              <b>{selected.name} <span>×</span> {track.name}</b>
            </div>
            <ActionButton variant="primary" label="Start Race" detail="Enter the start grid" onClick={() => controller.startRace()} />
          </div>
        }
      >
        <div className="car-selection-layout">
          <aside className="car-detail">
            <p className="eyebrow">{selected.tagline}</p>
            <h2>{selected.name}</h2>
            <p>{selected.description}</p>
            <StatBars stats={selected.stats} />
            <div className="car-detail__personality">
              <span>Driving character</span>
              <b>{selected.shape.archetype.replace('-', ' ')} / {selected.physics.lateralGrip < 5 ? 'loose' : selected.physics.stability > 8 ? 'planted' : 'responsive'}</b>
            </div>
          </aside>
          <div className="car-stage-copy" aria-hidden="true">
            <span>{selected.name}</span>
            <b>{selected.stats.topSpeed * 10}</b>
            <small>PERFORMANCE INDEX</small>
          </div>
          <div className="car-roster" role="list" aria-label="Cars">
            {CARS.map((car, index) => {
              const active = car.id === selected.id;
              return (
                <button
                  role="listitem"
                  key={car.id}
                  className={`car-roster-card ${active ? 'is-selected' : ''}`}
                  style={{ '--car-color': `#${car.primaryColor.toString(16).padStart(6, '0')}` } as React.CSSProperties}
                  onClick={() => controller.selectCar(car.id)}
                  aria-pressed={active}
                >
                  <span className="car-roster-card__index">0{index + 1}</span>
                  <span className="car-roster-card__silhouette" aria-hidden="true"><i /><i /><i /></span>
                  <b>{car.name}</b>
                  <small>{car.tagline}</small>
                  <StatBars stats={car.stats} compact />
                </button>
              );
            })}
          </div>
        </div>
      </MenuFrame>
    </div>
  );
}
